import React, { memo, ReactNode } from 'react';

import { Rate, Typography } from '@sravni/react-design-system';
import { PartnerBadge } from '@sravni/react-ugc/lib/PartnerBadge';
import pluralize from '@sravni/utils/lib/pluralize';

import { OrganizationType } from '../../../../../constants/organizationType';
import { sendGoToReviewsEvent } from '../../../../../helpers/analyticsEvents';
import { ICellItem } from '../../../../../interfaces/ratingTable';

import styles from './cell.module.scss';

type TCellProps = {
  item?: ICellItem;
  children?: ReactNode;
  tag?: string;
};

export const NumberCell = memo<TCellProps>(({ children }) => {
  return (
    <>
      <Typography.Text size={14} strong={true}>
        {children}
      </Typography.Text>
    </>
  );
});

export const NameCell = memo<TCellProps>(({ children, item }) => {
  const { squareLogoUrl, roundLogoUrl, logoUrl, isPartner } = item;

  const logo = <img src={squareLogoUrl || roundLogoUrl || logoUrl} alt={''} className={styles.logo} />;

  return (
    <div className={styles.nameLink}>
      {isPartner ? <PartnerBadge>{logo}</PartnerBadge> : logo}
      <span className={styles.name}>{children}</span>
    </div>
  );
});

export const RatingCell = memo<TCellProps>(({ item }) => {
  let ratingValue = item.rating;
  if (item.organizationType === OrganizationType.BANK && item.rating > 5) {
    ratingValue = 5;
  }

  return (
    <div>
      <Typography.Heading as={'span'} level={5}>
        {ratingValue.toFixed(2)}
      </Typography.Heading>
      <Rate count={1} value={1} disabled className={styles.star} />
    </div>
  );
});

export const ReviewsCountCell = memo<TCellProps>(({ children, item, tag }) => {
  const { reviewsUrl, organizationType, name } = item;

  const handleCellClick = (e) => {
    e.stopPropagation();
    sendGoToReviewsEvent({ organizationType, name });
  };

  return (
    <a
      href={tag ? `${reviewsUrl}?tag=${tag}` : reviewsUrl}
      className={styles.link}
      onClick={handleCellClick}
      target={'_blank'}
      rel="noreferrer"
    >
      {children}
    </a>
  );
});

export const ResponseCountCell = memo<TCellProps>(({ item, children, tag }) => {
  const { responseUrl, organizationType, name } = item;

  const handleCellClick = (e) => {
    e.stopPropagation();
    sendGoToReviewsEvent({ organizationType, name });
  };

  return (
    <>
      <a
        href={tag ? `${responseUrl}&tag=${tag}` : responseUrl}
        className={styles.link}
        onClick={handleCellClick}
        target={'_blank'}
        rel="noreferrer"
      >
        {children}
      </a>
    </>
  );
});

export const SolvedProblemsCell = memo<TCellProps>(({ children, item, tag }) => {
  const { solvedProblemsUrl, organizationType, name } = item;

  const handleCellClick = (e) => {
    e.stopPropagation();
    sendGoToReviewsEvent({ organizationType, name });
  };

  return (
    <>
      <a
        href={tag ? `${solvedProblemsUrl}&tag=${tag}` : solvedProblemsUrl}
        className={styles.link}
        onClick={handleCellClick}
        target={'_blank'}
        rel="noreferrer"
      >
        {children}
      </a>
    </>
  );
});

export const ResponseSpeedCell = memo<TCellProps>(({ item: { responseSpeed } }) => {
  const { days, hours } = responseSpeed;

  const d = days ? `${days} ${pluralize(days, 'день', 'дня', 'дней')}` : '';
  const h = hours ? `${hours} ${pluralize(hours, 'час', 'часа', 'часов')}` : '';

  const humanizedString = `${d}<br />${h}`.trim();

  const hasResponseTime = humanizedString !== '<br />';

  const html = {
    __html: hasResponseTime ? humanizedString : '&#8734;',
  };

  return (
    <>
      <Typography.Text size={hasResponseTime ? 14 : 20} strong={true} dangerouslySetInnerHTML={html} />
    </>
  );
});
